import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import StripePayment from './StripePayment';

const OveragePurchase = ({ currentPlan, onSuccess, onCancel }) => {
  const [additionalExecutions, setAdditionalExecutions] = useState(100);
  const [showPayment, setShowPayment] = useState(false);

  // Overage pricing per plan
  const overagePricing = {
    'STARTER': 0.50,
    'GROWTH': 0.40,
    'BUSINESS': 0.35,
    'ENTERPRISE': 0.30
  };

  const pricePerExecution = overagePricing[currentPlan] || 0.50;
  const totalCost = (additionalExecutions * pricePerExecution).toFixed(2);

  const handleIncrease = () => {
    setAdditionalExecutions(prev => Math.min(prev + 100, 10000));
  };

  const handleDecrease = () => {
    setAdditionalExecutions(prev => Math.max(prev - 100, 100));
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value) || 100;
    setAdditionalExecutions(Math.min(Math.max(value, 100), 10000));
  };

  if (showPayment) {
    return (
      <StripePayment
        isOverage={true}
        additionalExecutions={additionalExecutions}
        onSuccess={() => {
          setShowPayment(false);
          if (onSuccess) onSuccess();
        }}
        onCancel={() => setShowPayment(false)}
      />
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Purchase Additional Executions
        </h3>
        <p className="text-sm text-gray-600">
          Add more test executions to your {currentPlan.toLowerCase()} plan
        </p>
      </div>

      <div className="space-y-6">
        {/* Execution Counter */}
        <div className="bg-gray-50 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Number of Additional Executions
          </label>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDecrease}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              disabled={additionalExecutions <= 100}
            >
              <Minus className="h-4 w-4" />
            </button>
            
            <input
              type="number"
              value={additionalExecutions}
              onChange={handleInputChange}
              min="100"
              max="10000"
              step="100"
              className="flex-1 text-center text-lg font-bold border border-gray-300 rounded-lg p-2"
            />
            
            <button
              onClick={handleIncrease}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              disabled={additionalExecutions >= 10000}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Min: 100 executions • Max: 10,000 executions per purchase
          </p>
        </div>

        {/* Pricing Breakdown */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Price per execution:</span>
              <span className="font-medium">${pricePerExecution}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Quantity:</span>
              <span className="font-medium">{additionalExecutions.toLocaleString()}</span>
            </div>
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between text-lg font-bold">
                <span className="text-gray-900">Total:</span>
                <span className="text-purple-600">${totalCost}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-yellow-600 mr-3">💡</div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800">
                Consider upgrading your plan
              </h4>
              <p className="text-xs text-yellow-700 mt-1">
                Higher tier plans include more executions and better per-execution rates for overages.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => setShowPayment(true)}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 font-medium"
          >
            Purchase ${totalCost}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OveragePurchase;